import React, {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { hotjar } from 'react-hotjar';

export const HOTJAR_EVENT_NAME = {
  AUTO_GENERATE_REFUND_BARCODES_MODAL: 'AutoGenerateRefundBarcodesModal',
  CARRIER_MODAL: 'CarrierModal',
  CHOOSE_ACTOR_MODAL: 'ChooseActorModal',
  CHOOSE_ANY_ACTOR_MODAL: 'ChooseAnyActorModal',
  COMMERCIAL_SOLUTION_MODAL: 'commercialSolutionModal',
  CONFORMITY_RECEPTION_MODAL: 'ConformityReceiptionModal',
  CREATE_FILE_BY_CUSTOMER_INFORMATION: 'CreateFileByCustomerInformation',
  CREATE_FILE_BY_CUSTOMER_INFORMATION_IFRAME:
    'CreateFileByCustomerInformationIframe',
  CREATE_FILE_BY_ORDER_REFERENCE_MODAL: 'CreateFileByOrderReferenceModal',
  CREATE_FILE_BY_OWNER_STOCK_MODAL: 'CreateFileByOwnerStockModal',
  CREATE_FILE_BY_PRODUCT_REFERENCE_MODAL: 'CreateFileByProductReferenceModal',
  CREATE_FILE_BY_RECEIPT_INFORMATION_MODAL:
    'CreateFileByReceiptInformationModal',
  NEW_CREATE_FILE_BY_ORDER_REFERENCE: 'NewCreateFileByOrderReference',
  NEW_CREATE_FILE_BY_CUSTOMER_INFORMATION_IFRAME:
    'NewCreateFileByCustomerInformationIframe',
  NEW_CREATE_FILE_BY_CUSTOMER_INFORMATION: 'NewCreateFileByCustomerInformation',
  NEW_CREATE_FILE_BY_PRODUCT_REFERENCE: 'NewCreateFileByProductReference',
  NEW_CREATE_FILE_BY_PRODUCT_REFERENCE_WITHOUT_WARRANTY:
    'NewCreateFileByProductReferenceWithoutWarranty',
  CREATE_SELLER_MODAL: 'CreateSellerModal',
  CREATE_SPARE_PART_REQUEST_MODAL: 'CreateSparePartRequestModal',
  CREATE_TENANT_MODAL: 'CreateTenantModal',
  EXTERNAL_QUOTE_MODAL: 'ExternalQuoteModal',
  INTERNAL_QUOTE_MODAL: 'InternalQuoteModal',
  INTERVENTION_REPORT_MODAL: 'InterventionReportModal',
  PROCEDURE_MODAL: 'ProcedureModal',
  REFUND_CONFIRMATION_MODAL: 'RefundConfirmationModal',
  SPARE_PART_REQUEST_ORDER_COLLECTED_MODAL:
    'sparePartRequestOrderCollectedModal',
  SPARE_PART_REQUEST_ORDER_SUBMITTED_MODAL:
    'sparePartRequestOrderSubmittedModal',
  SPLIT_FILE_MODAL: 'splitFileModal',
  CREATE_FILE_FROM_ORDER_MODAL: 'CreateFileFromOrderModal',
  FINALIZE_CLAIM_MODAL: 'FinalizeClaimModal',
  SELECT_ISSUE: 'SelectIssue',
  SELECT_REASON: 'SelectReason',
  SELECT_SOLUTION: 'SelectSolution',
  SELECT_HANDLING_DEPOSIT: 'SelectHandlingDeposit',
  SELECT_HANDLING_DELIVERY: 'SelectHandlingDelivery',
  SELECT_HANDLING_AT_HOME: 'SelectHandlingAtHome',
  CREATE_FILE_FINISH_STEP: 'CreateFileFinishStep',
  FORCE_CLOSE_FILE: 'ForceCloseFile',
} as const;

export type HotjarEventName =
  (typeof HOTJAR_EVENT_NAME)[keyof typeof HOTJAR_EVENT_NAME];

export interface HotjarConfigurationEnabled {
  enabled: true;
  hjid: number;
  hjsv: number;
}
export interface HotjarConfigurationDisabled {
  enabled: false;
}

interface HotjarContextReturnValue {
  isHotjarEnabled: boolean;
  hotjar: typeof hotjar;
}

const HotjarProviderContext = React.createContext<HotjarContextReturnValue>({
  isHotjarEnabled: false,
  hotjar,
});

export const useHotjar = (): HotjarContextReturnValue => {
  return useContext(HotjarProviderContext);
};

export const useOnRegisterHotjarEventTracking = (): ((
  event: HotjarEventName,
) => boolean) => {
  const { isHotjarEnabled } = useHotjar();

  return useCallback(
    (event: string) => {
      if (isHotjarEnabled) {
        hotjar.event(event);

        return true;
      }

      return false;
    },
    [isHotjarEnabled],
  );
};

export const useHotjarEventTrackingOnMount = (
  event: HotjarEventName,
): HotjarContextReturnValue => {
  const { isHotjarEnabled } = useHotjar();

  useEffect(() => {
    if (isHotjarEnabled) {
      hotjar.event(event);
    }
  }, [event, isHotjarEnabled]);

  return useContext(HotjarProviderContext);
};

interface HotjarProviderProps {
  config: HotjarConfigurationEnabled | HotjarConfigurationDisabled;
}

const HotjarProvider: FunctionComponent<
  React.PropsWithChildren<HotjarProviderProps>
> = ({ children, config }) => {
  const [isHotjarEnabled, setIsHotjarEnabled] = useState(false);

  useLayoutEffect(() => {
    if (config.enabled) {
      hotjar.initialize(config.hjid, config.hjsv);
      setIsHotjarEnabled(true);
    }
  }, [config]);

  return (
    <HotjarProviderContext.Provider value={{ isHotjarEnabled, hotjar }}>
      {children}
    </HotjarProviderContext.Provider>
  );
};

export default HotjarProvider;
